import { useAtom } from 'jotai';
import * as React from 'react';

import * as icons from '@creditornot/alchemy-react/icons';
import { addInteractionEvent } from '@creditornot/browser-telemetry';
import { IconButton } from '@creditornot/cb-button';
import { Divider } from 'common/components/cookbook/Divider';
import { NavigationBar } from 'common/components/cookbook/NavigationBar/NavigationBar';
import { Stack } from 'common/components/cookbook/Stack';
import { ViewMeta } from 'common/components/cookbook/ViewMeta';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedWarehouse } from 'common/hooks/useSelectedWarehouse';
import { useSetSearch } from 'common/hooks/useSetSearch';
import { UserGuides } from 'features/user-guides';
import { ItemSearchInput } from 'features/warehouse/common/components/ItemSearchInput';
import { useWarehouseFeedbackToast } from 'features/warehouse/common/feedback-toast/useWarehouseFeedbackToast';

import { receivingRoute } from '../index';
import { automaticNavigationDisabledAtom } from '../state';
import { InboundLoadSearch } from './InboundLoadSearch/InboundLoadSearch';
import { RecentlyAddedItemsDrawer } from './RecentlyAddedItemsDrawer/RecentlyAddedItemsDrawer';

export const ReceivingView: React.FC = () => {
  const { id: selectedWarehouseId } = useSelectedWarehouse();

  const [isAutomaticNavigationDisabled, setIsAutomaticNavigationDisabled] = useAtom(
    automaticNavigationDisabledAtom,
  );

  const { showRecentlyAdded = false, showUserGuides = false } = receivingRoute.useSearch();

  const setSearch = useSetSearch(receivingRoute);
  const formatMessage = useFormatMessage();

  const viewTitle = formatMessage('receiving.receiving');

  const { isFeedbackToastShown } = useWarehouseFeedbackToast({
    eventName: 'receiving',
    feedbackTitle: formatMessage('receiving.feedback_title'),
  });

  return (
    <ViewMeta title={viewTitle} dataTestId="receiving">
      <NavigationBar
        title={viewTitle}
        showWarehouseSelector={true}
        hideBottomBorder={true}
        rightContent={
          <Stack direction="row" gap="regularXlarge">
            <IconButton
              icon={icons.documentCheckFilled}
              variant="neutral"
              size="small"
              onClick={() => {
                setSearch({ showRecentlyAdded: true });
                addInteractionEvent('receiving', 'click', {
                  venue_id: selectedWarehouseId,
                  click_target: 'recently_added_items',
                });
              }}
              data-test-id="open-recently-received-button"
            />
            <UserGuides
              context="RECEIVING"
              isVisible={showUserGuides}
              selectedWarehouseId={selectedWarehouseId}
              onClose={() => {
                setSearch({ showUserGuides: undefined });
              }}
            >
              <div style={{ position: 'relative' }}>
                <IconButton
                  icon={icons.questionCircleFilled}
                  variant="neutral"
                  size="small"
                  onClick={() => {
                    setSearch({ showUserGuides: true });
                    addInteractionEvent('receiving', 'click', {
                      venue_id: selectedWarehouseId,
                      click_target: 'user_guides',
                    });
                  }}
                />
              </div>
            </UserGuides>
          </Stack>
        }
      />
      <ItemSearchInput
        keydownCaptureDisabled={showRecentlyAdded || showUserGuides || isFeedbackToastShown}
        onChange={() => {
          setIsAutomaticNavigationDisabled(false);
          setSearch({ searchFromCatalog: undefined });
        }}
      >
        {(searchQuery, isSearchingByGtin) => {
          return (
            <>
              <Divider />
              <InboundLoadSearch
                selectedWarehouseId={selectedWarehouseId}
                searchQuery={searchQuery}
                isSearchingByGtin={isSearchingByGtin}
                isAutomaticNavigationDisabled={isAutomaticNavigationDisabled}
              />
            </>
          );
        }}
      </ItemSearchInput>
      <RecentlyAddedItemsDrawer
        selectedWarehouseId={selectedWarehouseId}
        isVisible={showRecentlyAdded}
        onDismiss={() => {
          addInteractionEvent('receiving', 'click', {
            venue_id: selectedWarehouseId,
            click_target: 'back',
          });
          setSearch({ showRecentlyAdded: false });
        }}
      />
    </ViewMeta>
  );
};
